import { AppType } from "client/Api/Apps/common";
import {
	Button,
	FormItem,
	Select,
	SelectItem,
	ValidationError
} from "client/Components";
import { AppDestinationWindowSelectFormItem } from "client/Features/Apps/AppDestinationWindowSelectFormItem";
import { IFormHandler, useFormHandler } from "client/Forms";
import {
	AlertManager,
	useAlerts,
	useUnmountedCancellation
} from "client/Hooks";
import { createValidationConfiguration } from "client/Validation";
import {
	isNotEmpty,
	isNotNullNorUndefined
} from "client/Validation/rules";
import {
	forwardRef,
	ReactElement,
	Ref,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useState
} from "react";
import {
	isCancelledError
} from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { EditAppHandle } from "../EditApp";
import {
	EditAppBase,
	EditAppSettings,
	initialGenericState
} from "../EditAppBase";
import { EditAppSavedResult, useSaveApp } from "../useSaveApp";
import {
	AfasConnectionSelectOptionsModel,
	getAfasConnections
} from "client/Api/Apps/EditApp/appParameters";
import styles from "./EditAfasApp.module.less";
import { AfasConnectionDialog } from "client/Features/Settings/Connections/Afas/AfasConnectionDialog";
import { AfasConnectionListItemModel } from "client/Api/AfasConfiguration/afasConfiguration";
import {
	SaveAfasAppModel,
	EditAfasAppViewModel,
	saveAfasApp
} from "client/Api/Apps/EditApp/saveApp";

const validation = createValidationConfiguration<SaveAfasAppModel>();

validation.forScalar("name")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty()
	);

validation.forScalar("connectionId")
	.add(
		isNotNullNorUndefined()
	);

export type EditAfasAppProps = {
	initialSettings: EditAppSettings<EditAfasAppViewModel>;
	initiallyActivateWhoHasAccessTab: boolean;
};

export const EditAfasApp = forwardRef(
	(
		props: EditAfasAppProps,
		ref: Ref<EditAppHandle>
	): ReactElement => {
		const {
			initialSettings,
			initiallyActivateWhoHasAccessTab
		} = props;

		const [afasConnectionSelectOptionsModel, setAfasConnectionSelectOptionsModel] =
			useState<AfasConnectionSelectOptionsModel>({
				canCreateNew: false,
				options: []
			});

		const [formData, setFormData] = useState<SaveAfasAppModel>(
			() => convertToSaveModel(initialSettings)
		);

		const connectionOptions: readonly SelectItem<number>[] = useMemo(
			(): readonly SelectItem<number>[] => {
				return afasConnectionSelectOptionsModel.options
					.map(
						o => ({
							value: o.id,
							text: o.name
						})
					);
			},
			[afasConnectionSelectOptionsModel.options]
		);

		const alertManager: AlertManager = useAlerts();
		const globalCancellation: AbortSignal = useUnmountedCancellation();
		const [isEditAfasDialogVisible, setIsEditAfasDialogVisible] = useState<boolean>(false);

		const {
			app,
			usersAndUserGroupsData
		} = initialSettings.model;

		const formHandler: IFormHandler<SaveAfasAppModel> = useFormHandler(
			formData,
			setFormData,
			validation
		);

		const loadAfasConnections = useCallback(
			async (signal: AbortSignal): Promise<void> => {
				try {
					const afasConnectionSelectOptionsModel: AfasConnectionSelectOptionsModel =
						await getAfasConnections(signal);

					setAfasConnectionSelectOptionsModel(afasConnectionSelectOptionsModel);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
				}
			},
			[alertManager]
		);

		const addConnectionHandler = useCallback(
			(): void => {
				setIsEditAfasDialogVisible(true);
			},
			[]
		);

		const afterSaveHandler = async (item: AfasConnectionListItemModel) => {
			await loadAfasConnections(globalCancellation);
			formHandler.for("connectionId")
				.attachToSelect()
				.onChange(item.id);
		};

		useEffect(
			() => {
				const cancellation = new AbortController();
				loadAfasConnections(cancellation.signal);

				return () => {
					cancellation.abort();
				};
			},
			[loadAfasConnections]
		);

		const save: () => Promise<EditAppSavedResult> = useSaveApp<SaveAfasAppModel>(
			formHandler,
			saveAfasApp,
			AppType.Afas,
		);

		useImperativeHandle(
			ref,
			(): EditAppHandle => ({
				save
			}),
			[save]
		);

		const hasValidationErrorForSpecificSettings: boolean | undefined = formHandler
			.under("connectionId", "destinationWindow")
			.attachToTab()
			.hasValidationError;

		return (
			<EditAppBase
				formHandler={formHandler}
				hasValidationErrorForSpecificSettings={hasValidationErrorForSpecificSettings}
				initialAppSettings={app}
				usersAndUserGroupsData={usersAndUserGroupsData}
				initiallyActivateWhoHasAccessTab={initiallyActivateWhoHasAccessTab}
			>
				<FormItem
					label={localize("_Connection")}
				>
					<div className={styles.connectionPickerRow}>
						<div className={styles.connectionPickerRowInputWrapper}>
							<Select
								{...formHandler.for("connectionId")
								.attachToSelect()}
								placeholder={localize("_SelectConnection")}
								options={connectionOptions}
								attachedOn={afasConnectionSelectOptionsModel.canCreateNew
									? "right"
									: undefined
								}
								menuTitle={localize("_Connection")}
							/>
						</div>
						{afasConnectionSelectOptionsModel.canCreateNew && (
							<Button
								kind="primary"
								text={localize("_Add")}
								attachedOn="left"
								onClick={addConnectionHandler}
								trackingId="add-new-connection-button"
							/>
						)}
					</div>
					<ValidationError
						{...formHandler.for("connectionId")
							.attachToValidationError()}
					/>
				</FormItem>
				<AppDestinationWindowSelectFormItem
					attachable={formHandler.for("destinationWindow")}
				/>
				{
					isEditAfasDialogVisible && (
						<AfasConnectionDialog
							onAfterSave={(item) => afterSaveHandler(item)}
							onClose={() => setIsEditAfasDialogVisible(false)}
							connectionId={undefined}
						/>
					)
				}
			</EditAppBase>
		);
	}
);

EditAfasApp.displayName = "EditAfasApp";

function convertToSaveModel(
	initialSettings: EditAppSettings<EditAfasAppViewModel>
): SaveAfasAppModel {
	const {
		connectionId,
		destinationWindow
	} = initialSettings
		.model
		.app;

	return {
		...initialGenericState(initialSettings),
		connectionId,
		destinationWindow,
	};
}

